import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  ChartBarIcon,
  DocumentTextIcon,
  TrashIcon,
  CalendarIcon,
  ClockIcon,
  LightBulbIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  StopIcon,
  BookmarkIcon,
  FireIcon,
  ArrowPathIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { marked } from 'marked';
import { API_BASE_URL } from '../config/api';
import { exportAPI } from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

// Interfaces
interface ExerciseSet {
  id?: string;
  set_number: number;
  weight_value?: number;
  weight_unit?: string;
  reps?: number;
  distance_value?: number;
  distance_unit?: string;
  duration_seconds?: number;
  pace_per_unit?: number;
  heart_rate?: number;
  calories?: number;
  resistance_level?: number;
  resistance_type?: string;
  equipment?: string;
  rest_seconds?: number;
  notes?: string;
}

interface ExerciseFocus {
  id?: string;
  category: string;
  muscle_group?: string;
  exercise_name: string;
  order_index: number;
  notes?: string;
  sets: ExerciseSet[];
  exercise_date?: string;
  exercise_time?: string;
  exercise_duration?: string; // Format: "HH:MM"
}

interface ExerciseSession {
  id: string;
  start_time: string;
  end_time?: string;
  exercise_type: string;
  notes?: string;
  total_duration_minutes?: number;
  total_calories?: number;
  heart_rate_avg?: number;
  heart_rate_max?: number;
  focuses?: ExerciseFocus[];
}

interface WorkoutTemplate {
  id: string;
  name: string;
  description?: string;
  exercise_type: string;
  is_public: boolean;
  template_data: {
    focuses: ExerciseFocus[];
  };
  created_at: string;
}

// Reference data types from API
interface ExerciseTypeOption {
  id: string;
  name: string;
}

interface EquipmentOption {
  id: string;
  name: string;
  category: string;
}

interface MuscleGroupOption {
  id: string;
  name: string;
  exercises: string[];
}

interface ExerciseAnalysis {
  summary: string;
  patterns_identified: string[];
  progress_insights: string[];
  recommendations: string[];
  recovery_suggestions: string[];
  resource_links: { title: string; url: string; source: string }[];
  analysis_period_days: number;
  total_sessions_analyzed: number;
}

interface ExerciseStatistics {
  total_sessions: number;
  total_duration_minutes: number;
  total_calories: number;
  avg_session_duration: number;
  sessions_by_type: Record<string, number>;
  sessions_by_category: Record<string, number>;
  recent_sessions: number;
  streak_days: number;
}

type TabType = 'log' | 'history' | 'charts' | 'analysis' | 'templates';

// Color constants
const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00C49F'];

export const ExerciseTracker: React.FC = () => {
  const { t } = useTranslation();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('log');

  // Session form state
  const [sessionNotes, setSessionNotes] = useState('');
  const [heartRateAvg, setHeartRateAvg] = useState<number | undefined>();
  const [heartRateMax, setHeartRateMax] = useState<number | undefined>();
  const [totalCalories, setTotalCalories] = useState<number | undefined>();

  // Focuses and sets
  const [focuses, setFocuses] = useState<ExerciseFocus[]>([]);
  const [expandedFocus, setExpandedFocus] = useState<number | null>(0);

  // Active session state
  const [activeSession, setActiveSession] = useState<ExerciseSession | null>(null);
  const [sessionTimer, setSessionTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);

  // Data state
  const [exerciseTypes, setExerciseTypes] = useState<ExerciseTypeOption[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<EquipmentOption[]>([]);
  const [muscleGroups, setMuscleGroups] = useState<MuscleGroupOption[]>([]);
  const [sessionHistory, setSessionHistory] = useState<ExerciseSession[]>([]);
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [statistics, setStatistics] = useState<ExerciseStatistics | null>(null);
  const [analysis, setAnalysis] = useState<ExerciseAnalysis | null>(null);
  const [historyDays, setHistoryDays] = useState(30);

  // Template form state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<WorkoutTemplate | null>(null);

  // Report state
  const [reportContent, setReportContent] = useState<string | null>(null);
  const [reportTitle, setReportTitle] = useState('Exercise Report');

  // History expanded session state
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [expandedSessionDetails, setExpandedSessionDetails] = useState<Record<string, ExerciseSession>>({});

  // Chart period state
  const [chartPeriod, setChartPeriod] = useState<'week' | 'month' | 'ytd'>('week');

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Category options
  const categoryOptions = [
    { id: 'cardio', name: t('exerciseTracker:categories.cardio') },
    { id: 'weights', name: t('exerciseTracker:categories.weights') },
    { id: 'stretching', name: t('exerciseTracker:categories.stretching') },
    { id: 'other', name: t('exerciseTracker:categories.other') },
  ];

  // Load reference data on mount
  useEffect(() => {
    fetchExerciseTypes();
    fetchEquipmentOptions();
    fetchMuscleGroups();
    fetchSessionHistory();
    addEmptyFocus();
  }, []);

  // Load history when tab changes or days change
  useEffect(() => {
    if (activeTab === 'history' || activeTab === 'charts') {
      fetchSessionHistory();
    }
    if (activeTab === 'charts') {
      fetchStatistics();
    }
    if (activeTab === 'templates') {
      fetchTemplates();
    }
  }, [activeTab, historyDays]);

  // Timer effect
  useEffect(() => {
    if (activeSession && !timerInterval) {
      const startTime = new Date(activeSession.start_time).getTime();
      const interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setSessionTimer(elapsed);
      }, 1000);
      setTimerInterval(interval);
    }
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [activeSession]);

  // API calls
  const fetchExerciseTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/exercise-tracker/exercise-types`);
      if (response.ok) {
        const data = await response.json();
        setExerciseTypes(data.exercise_types || []);
      }
    } catch (error) {
      console.error('Failed to fetch exercise types:', error);
    }
  };

  const fetchEquipmentOptions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/exercise-tracker/equipment`);
      if (response.ok) {
        const data = await response.json();
        setEquipmentOptions(data.equipment || []);
      }
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
    }
  };

  const fetchMuscleGroups = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/exercise-tracker/muscle-groups`);
      if (response.ok) {
        const data = await response.json();
        setMuscleGroups(data.muscle_groups || []);
      }
    } catch (error) {
      console.error('Failed to fetch muscle groups:', error);
    }
  };

  const fetchSessionHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/exercise-tracker/sessions?days=${historyDays}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSessionHistory(data.sessions);
      }
    } catch (error) {
      console.error('Failed to fetch session history:', error);
    }
  };

  const fetchSessionDetails = async (sessionId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/exercise-tracker/sessions/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setExpandedSessionDetails((prev) => ({
          ...prev,
          [sessionId]: data.session,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch session details:', error);
    }
  };

  const toggleSessionExpand = (sessionId: string) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
    } else {
      setExpandedSessionId(sessionId);
      // Fetch details if not already loaded
      if (!expandedSessionDetails[sessionId]) {
        fetchSessionDetails(sessionId);
      }
    }
  };

  const formatTimeShort = (timeString?: string) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDurationMinutes = (minutes?: number) => {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/exercise-tracker/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/exercise-tracker/statistics?days=${historyDays}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  // Helper to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  // Helper to get current time in HH:MM format
  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  // Focus and Set management
  const addEmptyFocus = () => {
    const newFocus: ExerciseFocus = {
      category: 'weights',
      exercise_name: '',
      order_index: focuses.length,
      sets: [createEmptySet(1)],
      exercise_date: getCurrentDate(),
      exercise_time: getCurrentTime(),
      exercise_duration: '00:30',
    };
    setFocuses([...focuses, newFocus]);
    setExpandedFocus(focuses.length);
  };

  const createEmptySet = (setNumber: number): ExerciseSet => ({
    set_number: setNumber,
    weight_unit: 'lbs',
    distance_unit: 'miles',
  });

  const updateFocus = (index: number, updates: Partial<ExerciseFocus>) => {
    const newFocuses = [...focuses];
    newFocuses[index] = { ...newFocuses[index], ...updates };
    setFocuses(newFocuses);
  };

  const removeFocus = (index: number) => {
    if (focuses.length > 1) {
      const newFocuses = focuses.filter((_, i) => i !== index);
      setFocuses(newFocuses.map((f, i) => ({ ...f, order_index: i })));
      if (expandedFocus === index) {
        setExpandedFocus(null);
      } else if (expandedFocus !== null && expandedFocus > index) {
        setExpandedFocus(expandedFocus - 1);
      }
    }
  };

  const addSetToFocus = (focusIndex: number) => {
    const newFocuses = [...focuses];
    const focus = newFocuses[focusIndex];
    focus.sets.push(createEmptySet(focus.sets.length + 1));
    setFocuses(newFocuses);
  };

  const updateSet = (focusIndex: number, setIndex: number, updates: Partial<ExerciseSet>) => {
    const newFocuses = [...focuses];
    newFocuses[focusIndex].sets[setIndex] = {
      ...newFocuses[focusIndex].sets[setIndex],
      ...updates,
    };
    setFocuses(newFocuses);
  };

  const removeSet = (focusIndex: number, setIndex: number) => {
    const newFocuses = [...focuses];
    if (newFocuses[focusIndex].sets.length > 1) {
      newFocuses[focusIndex].sets = newFocuses[focusIndex].sets
        .filter((_, i) => i !== setIndex)
        .map((s, i) => ({ ...s, set_number: i + 1 }));
      setFocuses(newFocuses);
    }
  };

  // Helper to derive session exercise type from focuses
  const deriveExerciseType = (): string => {
    const validFocuses = focuses.filter(f => f.exercise_name.trim());
    if (validFocuses.length === 0) return 'mixed';
    if (validFocuses.length === 1) return validFocuses[0].exercise_name;
    const uniqueTypes = new Set(validFocuses.map(f => f.exercise_name));
    return uniqueTypes.size === 1 ? validFocuses[0].exercise_name : 'mixed';
  };

  // Session management
  const startSession = async () => {
    const validFocuses = focuses.filter(f => f.exercise_name.trim());
    if (validFocuses.length === 0) {
      setError(t('exerciseTracker:errors.exerciseRequired'));
      return;
    }

    setLoading(true);
    setLoadingMessage(t('exerciseTracker:loading.startingSession'));
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('exerciseTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const sessionExerciseType = deriveExerciseType();
      const response = await fetch(`${API_BASE_URL}/exercise-tracker/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          exercise_type: sessionExerciseType,
          notes: sessionNotes || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start session');
      }

      const data = await response.json();
      setActiveSession(data);
      setSessionTimer(0);
      setSuccess(t('exerciseTracker:success.sessionStarted'));
    } catch (error) {
      console.error('Start session error:', error);
      setError(t('exerciseTracker:errors.startFailed'));
    } finally {
      setLoading(false);
    }
  };

  const endSession = async () => {
    if (!activeSession) return;

    setLoading(true);
    setLoadingMessage(t('exerciseTracker:loading.savingSession'));
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('exerciseTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      // First, add all focuses and sets
      for (const focus of focuses) {
        if (!focus.exercise_name.trim()) continue;

        const focusResponse = await fetch(
          `${API_BASE_URL}/exercise-tracker/sessions/${activeSession.id}/focuses`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              category: focus.category,
              muscle_group: focus.muscle_group || null,
              exercise_name: focus.exercise_name,
              order_index: focus.order_index,
              notes: focus.notes || null,
            }),
          }
        );

        if (focusResponse.ok) {
          const focusData = await focusResponse.json();

          // Add sets to this focus
          for (const set of focus.sets) {
            await fetch(`${API_BASE_URL}/exercise-tracker/focuses/${focusData.id}/sets`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                set_number: set.set_number,
                weight_value: set.weight_value || null,
                weight_unit: set.weight_unit || 'lbs',
                reps: set.reps || null,
                distance_value: set.distance_value || null,
                distance_unit: set.distance_unit || 'miles',
                duration_seconds: set.duration_seconds || null,
                pace_per_unit: set.pace_per_unit || null,
                heart_rate: set.heart_rate || null,
                calories: set.calories || null,
                resistance_level: set.resistance_level || null,
                resistance_type: set.resistance_type || null,
                equipment: set.equipment || null,
                rest_seconds: set.rest_seconds || null,
                notes: set.notes || null,
              }),
            });
          }
        }
      }

      // End the session
      const endResponse = await fetch(
        `${API_BASE_URL}/exercise-tracker/sessions/${activeSession.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            end_session: true,
            notes: sessionNotes || null,
            total_calories: totalCalories || null,
            heart_rate_avg: heartRateAvg || null,
            heart_rate_max: heartRateMax || null,
          }),
        }
      );

      if (!endResponse.ok) {
        throw new Error('Failed to end session');
      }

      // Clear timer
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }

      setSuccess(t('exerciseTracker:success.sessionSaved'));
      resetForm();
      setActiveSession(null);
      fetchSessionHistory();
    } catch (error) {
      console.error('End session error:', error);
      setError(t('exerciseTracker:errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSessionNotes('');
    setHeartRateAvg(undefined);
    setHeartRateMax(undefined);
    setTotalCalories(undefined);
    setFocuses([]);
    addEmptyFocus();
    setExpandedFocus(0);
  };

  // Helper to convert duration string "HH:MM" to total minutes
  const durationToMinutes = (duration?: string): number => {
    if (!duration) return 30;
    const parts = duration.split(':');
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    return hours * 60 + minutes;
  };

  // Quick log (no timer)
  const quickLogSession = async () => {
    const validFocuses = focuses.filter(f => f.exercise_name.trim());
    if (validFocuses.length === 0) {
      setError(t('exerciseTracker:errors.exerciseRequired'));
      return;
    }

    setLoading(true);
    setLoadingMessage(t('exerciseTracker:loading.savingSession'));
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('exerciseTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const sessionExerciseType = deriveExerciseType();
      // Use date/time from the first focus
      const firstFocus = validFocuses[0];

      // Build start date from date and time inputs
      let startDate: Date;
      if (firstFocus.exercise_date && firstFocus.exercise_time) {
        startDate = new Date(`${firstFocus.exercise_date}T${firstFocus.exercise_time}:00`);
      } else {
        startDate = new Date();
      }

      // Build end date from date and end time inputs
      let endDate: Date;
      if (firstFocus.exercise_date && firstFocus.exercise_duration) {
        endDate = new Date(`${firstFocus.exercise_date}T${firstFocus.exercise_duration}:00`);
        // If end time is before start time, assume it's the next day
        if (endDate <= startDate) {
          endDate.setDate(endDate.getDate() + 1);
        }
      } else {
        // Default to 30 minutes after start
        endDate = new Date(startDate.getTime() + 30 * 60 * 1000);
      }

      // Build focuses array with sets for single API call
      const focusesPayload = validFocuses.map((focus, idx) => ({
        category: focus.category,
        muscle_group: focus.muscle_group || null,
        exercise_name: focus.exercise_name,
        order_index: idx,
        notes: focus.notes || null,
        sets: focus.sets.map((set, setIdx) => ({
          set_number: setIdx + 1,
          weight_value: set.weight_value || null,
          weight_unit: set.weight_unit || 'lbs',
          reps: set.reps || null,
          distance_value: set.distance_value || null,
          distance_unit: set.distance_unit || 'miles',
          duration_seconds: set.duration_seconds || null,
          pace_per_unit: set.pace_per_unit || null,
          heart_rate: set.heart_rate || null,
          calories: set.calories || null,
          resistance_level: set.resistance_level || null,
          resistance_type: set.resistance_type || null,
          equipment: set.equipment || null,
          rest_seconds: set.rest_seconds || null,
          notes: set.notes || null,
        })),
      }));

      // Create session with all focuses in one call
      const sessionResponse = await fetch(`${API_BASE_URL}/exercise-tracker/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          exercise_type: sessionExerciseType,
          notes: sessionNotes || null,
          start_time: startDate.toISOString(),
          end_time: endDate.toISOString(),
          total_calories: totalCalories || null,
          heart_rate_avg: heartRateAvg || null,
          heart_rate_max: heartRateMax || null,
          focuses: focusesPayload,
        }),
      });

      if (!sessionResponse.ok) {
        const errorText = await sessionResponse.text();
        console.error('Session creation failed:', errorText);
        throw new Error('Failed to create session');
      }

      setSuccess(t('exerciseTracker:success.sessionSaved'));
      resetForm();
      fetchSessionHistory();
    } catch (error) {
      console.error('Quick log error:', error);
      setError(t('exerciseTracker:errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Delete session
  const handleDeleteSession = async (sessionId: string) => {
    if (!window.confirm(t('exerciseTracker:confirm.deleteSession'))) return;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/exercise-tracker/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setSessionHistory(sessionHistory.filter((s) => s.id !== sessionId));
        setSuccess(t('exerciseTracker:success.sessionDeleted'));
      }
    } catch (error) {
      console.error('Delete session error:', error);
      setError(t('exerciseTracker:errors.deleteFailed'));
    }
  };

  // Template management
  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      setError(t('exerciseTracker:errors.templateNameRequired'));
      return;
    }

    const validFocuses = focuses.filter(f => f.exercise_name.trim());
    if (validFocuses.length === 0) {
      setError(t('exerciseTracker:errors.exerciseRequired'));
      return;
    }

    setLoading(true);
    setLoadingMessage(t('exerciseTracker:loading.savingTemplate'));

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('exerciseTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const templateExerciseType = deriveExerciseType();
      const templateData = {
        name: templateName,
        description: templateDescription || null,
        exercise_type: templateExerciseType,
        template_data: {
          focuses: validFocuses.map(f => ({
            category: f.category,
            muscle_group: f.muscle_group,
            exercise_name: f.exercise_name,
            order_index: f.order_index,
            sets: f.sets.map(s => ({
              set_number: s.set_number,
              weight_unit: s.weight_unit,
              distance_unit: s.distance_unit,
              equipment: s.equipment,
            })),
          })),
        },
      };

      const response = await fetch(`${API_BASE_URL}/exercise-tracker/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(templateData),
      });

      if (!response.ok) {
        throw new Error('Failed to save template');
      }

      setSuccess(t('exerciseTracker:success.templateSaved'));
      setShowTemplateForm(false);
      setTemplateName('');
      setTemplateDescription('');
      fetchTemplates();
    } catch (error) {
      console.error('Save template error:', error);
      setError(t('exerciseTracker:errors.templateSaveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = async (template: WorkoutTemplate) => {
    const newFocuses: ExerciseFocus[] = template.template_data.focuses.map((f, index) => ({
      category: f.category,
      muscle_group: f.muscle_group,
      exercise_name: f.exercise_name,
      order_index: index,
      sets: f.sets?.map((s, sIndex) => ({
        set_number: sIndex + 1,
        weight_unit: s.weight_unit || 'lbs',
        distance_unit: s.distance_unit || 'miles',
        equipment: s.equipment,
      })) || [createEmptySet(1)],
    }));

    setFocuses(newFocuses);
    setExpandedFocus(0);
    setActiveTab('log');
    setSuccess(t('exerciseTracker:success.templateApplied'));
  };

  const deleteTemplate = async (templateId: string) => {
    if (!window.confirm(t('exerciseTracker:confirm.deleteTemplate'))) return;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/exercise-tracker/templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setTemplates(templates.filter((t) => t.id !== templateId));
        setSuccess(t('exerciseTracker:success.templateDeleted'));
      }
    } catch (error) {
      console.error('Delete template error:', error);
      setError(t('exerciseTracker:errors.templateDeleteFailed'));
    }
  };

  // Analysis
  const handleAnalyze = async () => {
    setLoading(true);
    setLoadingMessage(t('exerciseTracker:loading.analyzing'));
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('exerciseTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/exercise-tracker/analyze?days=${historyDays}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to analyze');
      }

      const data = await response.json();
      setAnalysis(data);
    } catch (error) {
      console.error('Analyze error:', error);
      setError(t('exerciseTracker:errors.analysisFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Report generation
  const handleGenerateReport = async () => {
    setLoading(true);
    setLoadingMessage(t('exerciseTracker:loading.generatingReport'));
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError(t('exerciseTracker:errors.loginRequired'));
        setLoading(false);
        return;
      }

      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - historyDays * 24 * 60 * 60 * 1000).toISOString();

      const response = await fetch(
        `${API_BASE_URL}/exercise-tracker/generate-report?start_date=${startDate}&end_date=${endDate}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      const data = await response.json();
      setReportContent(data.report);
      setReportTitle(`Exercise Report - ${new Date().toLocaleDateString()}`);
    } catch (error) {
      console.error('Generate report error:', error);
      setError(t('exerciseTracker:errors.reportFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Export functions
  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    if (!reportContent) return;
    await exportDocument(format);
  };

  const printReport = () => {
    if (!reportContent) return;

    const htmlContent = marked(reportContent, { async: false }) as string;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; margin-bottom: 10px; }
            h2 { font-size: 20px; margin-top: 20px; margin-bottom: 10px; }
            h3 { font-size: 16px; margin-top: 16px; margin-bottom: 8px; }
            p { margin: 12px 0; }
            ul, ol { margin-left: 24px; }
            li { margin-bottom: 4px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>${reportTitle}</h1>
          <div class="content">${htmlContent}</div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportDocument = async (format: 'word' | 'pdf' | 'text') => {
    if (!reportContent) return;

    try {
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `${reportTitle.toLowerCase().replace(/\s+/g, '-')}.${extension}`;

      const blob = await exportAPI.exportDocument({
        content: reportContent,
        title: reportTitle,
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: format === 'word' ? 'Word Document' : format === 'pdf' ? 'PDF Document' : 'Text File',
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') return;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError(t('exerciseTracker:errors.exportFailed'));
    }
  };

  // Generate markdown from analysis data
  const generateAnalysisMarkdown = useCallback(() => {
    if (!analysis) return '';

    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    let markdown = `# Exercise Analysis Report\n\n`;
    markdown += `**Generated:** ${today}\n`;
    markdown += `**Period:** ${analysis.analysis_period_days} days\n`;
    markdown += `**Sessions Analyzed:** ${analysis.total_sessions_analyzed}\n\n`;

    markdown += `## Summary\n\n${analysis.summary}\n\n`;

    if (analysis.patterns_identified && analysis.patterns_identified.length > 0) {
      markdown += `## Patterns Identified\n\n`;
      analysis.patterns_identified.forEach(pattern => {
        markdown += `- ${pattern}\n`;
      });
      markdown += `\n`;
    }

    if (analysis.progress_insights && analysis.progress_insights.length > 0) {
      markdown += `## Progress Insights\n\n`;
      analysis.progress_insights.forEach(insight => {
        markdown += `- ${insight}\n`;
      });
      markdown += `\n`;
    }

    if (analysis.recommendations && analysis.recommendations.length > 0) {
      markdown += `## Recommendations\n\n`;
      analysis.recommendations.forEach(rec => {
        markdown += `- ${rec}\n`;
      });
      markdown += `\n`;
    }

    if (analysis.recovery_suggestions && analysis.recovery_suggestions.length > 0) {
      markdown += `## Recovery Suggestions\n\n`;
      analysis.recovery_suggestions.forEach(suggestion => {
        markdown += `- ${suggestion}\n`;
      });
      markdown += `\n`;
    }

    if (analysis.resource_links && analysis.resource_links.length > 0) {
      markdown += `## Fitness Resources\n\n`;
      analysis.resource_links.forEach(link => {
        markdown += `- [${link.title}](${link.url}) — ${link.source}\n`;
      });
      markdown += `\n`;
    }

    markdown += `---\n\n`;
    markdown += `*This analysis is for informational purposes only. Always consult a healthcare professional before starting a new exercise program.*\n`;

    return markdown;
  }, [analysis]);

  // Export analysis functions
  const handleExportAnalysis = async (format: 'word' | 'pdf' | 'text') => {
    if (!analysis) return;

    const content = generateAnalysisMarkdown();
    const title = 'Exercise Analysis Report';

    try {
      const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
      const extension = extensionMap[format];
      const filename = `exercise-analysis-report.${extension}`;

      const blob = await exportAPI.exportDocument({
        content,
        title,
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: format === 'word' ? 'Word Document' : format === 'pdf' ? 'PDF Document' : 'Text File',
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') return;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export analysis error:', error);
      setError(t('exerciseTracker:errors.exportFailed'));
    }
  };

  const printAnalysis = () => {
    if (!analysis) return;

    const content = generateAnalysisMarkdown();
    const htmlContent = marked(content, { async: false }) as string;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Exercise Analysis Report</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; margin-bottom: 10px; color: #111827; }
            h2 { font-size: 18px; margin-top: 24px; margin-bottom: 12px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }
            p { margin: 12px 0; }
            ul { margin-left: 24px; margin-bottom: 16px; }
            li { margin-bottom: 8px; }
            a { color: #4f46e5; text-decoration: none; }
            hr { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
            em { color: #6b7280; font-size: 14px; }
            @media print {
              body { padding: 20px; }
              a { color: #111827; }
            }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Utility functions
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Chart data preparation
  const getPeriodStats = () => {
    const today = new Date();
    let filteredSessions: ExerciseSession[] = [];

    if (chartPeriod === 'week') {
      // Last 7 days
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6);
      weekAgo.setHours(0, 0, 0, 0);
      filteredSessions = sessionHistory.filter(session => {
        const sessionDate = new Date(session.start_time);
        return sessionDate >= weekAgo;
      });
    } else if (chartPeriod === 'month') {
      // Current month
      const year = today.getFullYear();
      const month = today.getMonth();
      filteredSessions = sessionHistory.filter(session => {
        const sessionDate = new Date(session.start_time);
        return sessionDate.getFullYear() === year && sessionDate.getMonth() === month;
      });
    } else {
      // YTD
      const year = today.getFullYear();
      filteredSessions = sessionHistory.filter(session => {
        const sessionDate = new Date(session.start_time);
        return sessionDate.getFullYear() === year;
      });
    }

    // Calculate total sessions
    const totalSessions = filteredSessions.length;

    // Calculate total duration from start_time and end_time
    let totalDurationMinutes = 0;
    for (const session of filteredSessions) {
      if (session.start_time && session.end_time) {
        const startDt = new Date(session.start_time);
        const endDt = new Date(session.end_time);
        const durationMinutes = (endDt.getTime() - startDt.getTime()) / (1000 * 60);
        totalDurationMinutes += Math.round(durationMinutes);
      } else if (session.total_duration_minutes) {
        totalDurationMinutes += session.total_duration_minutes;
      }
    }

    // Calculate total calories
    const totalCalories = filteredSessions.reduce((sum, s) => sum + (s.total_calories || 0), 0);

    return { totalSessions, totalDurationMinutes, totalCalories };
  };

  const getSessionsByTypeData = () => {
    if (!statistics?.sessions_by_type) return [];
    return Object.entries(statistics.sessions_by_type).map(([name, value]) => ({
      name,
      value,
    }));
  };

  const getSessionsByCategoryData = () => {
    if (!statistics?.sessions_by_category) return [];
    return Object.entries(statistics.sessions_by_category).map(([name, value]) => ({
      name: t(`exerciseTracker:categories.${name}`, name),
      value,
    }));
  };

  const getActivityChartData = () => {
    const today = new Date();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (chartPeriod === 'week') {
      // Show last 7 days with DOW and mm/dd
      const data: { label: string; sessions: number }[] = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dayName = dayNames[date.getDay()];
        const monthDay = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
        const dateStr = date.toISOString().split('T')[0];

        const sessionsOnDay = sessionHistory.filter(session => {
          const sessionDate = new Date(session.start_time).toISOString().split('T')[0];
          return sessionDate === dateStr;
        }).length;

        data.push({ label: `${dayName}\n${monthDay}`, sessions: sessionsOnDay });
      }
      return data;
    } else if (chartPeriod === 'month') {
      // Show current month by week
      const year = today.getFullYear();
      const month = today.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const data: { label: string; sessions: number }[] = [];

      // Group by week of the month
      let weekStart = new Date(firstDay);
      let weekNum = 1;
      while (weekStart <= lastDay) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        if (weekEnd > lastDay) weekEnd.setTime(lastDay.getTime());

        const startStr = `${(weekStart.getMonth() + 1)}/${weekStart.getDate()}`;
        const endStr = `${(weekEnd.getMonth() + 1)}/${weekEnd.getDate()}`;

        const sessionsInWeek = sessionHistory.filter(session => {
          const sessionDate = new Date(session.start_time);
          return sessionDate >= weekStart && sessionDate <= weekEnd;
        }).length;

        data.push({ label: `Week ${weekNum}\n${startStr}-${endStr}`, sessions: sessionsInWeek });

        weekStart = new Date(weekEnd);
        weekStart.setDate(weekStart.getDate() + 1);
        weekNum++;
      }
      return data;
    } else {
      // YTD - show by month
      const year = today.getFullYear();
      const currentMonth = today.getMonth();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const data: { label: string; sessions: number }[] = [];

      for (let month = 0; month <= currentMonth; month++) {
        const sessionsInMonth = sessionHistory.filter(session => {
          const sessionDate = new Date(session.start_time);
          return sessionDate.getFullYear() === year && sessionDate.getMonth() === month;
        }).length;

        data.push({ label: monthNames[month], sessions: sessionsInMonth });
      }
      return data;
    }
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <FireIcon className="h-12 w-12 text-orange-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('exerciseTracker:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('exerciseTracker:subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex-wrap gap-1">
          {(['log', 'history', 'charts', 'analysis', 'templates'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {tab === 'log' && <PlusIcon className="h-4 w-4 inline mr-1" />}
              {tab === 'history' && <CalendarIcon className="h-4 w-4 inline mr-1" />}
              {tab === 'charts' && <ChartBarIcon className="h-4 w-4 inline mr-1" />}
              {tab === 'analysis' && <LightBulbIcon className="h-4 w-4 inline mr-1" />}
              {tab === 'templates' && <BookmarkIcon className="h-4 w-4 inline mr-1" />}
              {t(`exerciseTracker:tabs.${tab}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-4xl mx-auto w-full mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="max-w-4xl mx-auto w-full mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-green-700 dark:text-green-300 text-sm">{success}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto max-w-4xl mx-auto w-full">
        {/* Log Tab */}
        {activeTab === 'log' && (
          <div className="space-y-6">
            {/* Active Session Timer */}
            {activeSession && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
                    <span className="font-medium text-orange-800 dark:text-orange-200">
                      {t('exerciseTracker:session.active')}
                    </span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-orange-600 dark:text-orange-400">
                    {formatDuration(sessionTimer)}
                  </div>
                </div>
              </div>
            )}

            {/* Session Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="space-y-6">
                {/* Focuses/Exercises */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('exerciseTracker:form.exercises')}
                    </label>
                    <button
                      type="button"
                      onClick={addEmptyFocus}
                      className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 flex items-center gap-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      {t('exerciseTracker:form.addExercise')}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {focuses.map((focus, focusIndex) => (
                      <div
                        key={focusIndex}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                      >
                        {/* Focus Header */}
                        <div
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 cursor-pointer"
                          onClick={() => setExpandedFocus(expandedFocus === focusIndex ? null : focusIndex)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              #{focusIndex + 1}
                            </span>
                            <select
                              value={focus.exercise_name}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateFocus(focusIndex, { exercise_name: e.target.value });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-orange-500 text-sm"
                            >
                              <option value="">{t('exerciseTracker:form.selectType')}</option>
                              {exerciseTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </select>
                            <select
                              value={focus.category}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateFocus(focusIndex, { category: e.target.value });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                            >
                              {categoryOptions.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-center gap-2">
                            {focuses.length > 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFocus(focusIndex);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                            {expandedFocus === focusIndex ? (
                              <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                            )}
                          </div>
                        </div>

                        {/* Focus Details (expanded) */}
                        {expandedFocus === focusIndex && (
                          <div className="p-4 space-y-4">
                            {/* Date, Time, Duration & Muscle Group Row */}
                            <div className="grid grid-cols-4 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('exerciseTracker:form.date')}
                                </label>
                                <input
                                  type="date"
                                  value={focus.exercise_date || getCurrentDate()}
                                  onChange={(e) => updateFocus(focusIndex, { exercise_date: e.target.value })}
                                  className="w-full px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('exerciseTracker:form.time')}
                                </label>
                                <input
                                  type="time"
                                  value={focus.exercise_time || getCurrentTime()}
                                  onChange={(e) => updateFocus(focusIndex, { exercise_time: e.target.value })}
                                  className="w-full px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('exerciseTracker:form.duration')}
                                </label>
                                <input
                                  type="time"
                                  value={focus.exercise_duration || '00:30'}
                                  onChange={(e) => updateFocus(focusIndex, { exercise_duration: e.target.value })}
                                  className="w-full px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  {t('exerciseTracker:form.muscleGroup')}
                                </label>
                                <select
                                  value={focus.muscle_group || ''}
                                  onChange={(e) => updateFocus(focusIndex, { muscle_group: e.target.value || undefined })}
                                  className="w-full px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                >
                                  <option value="">{t('exerciseTracker:form.selectMuscle')}</option>
                                  {muscleGroups.map((mg) => (
                                    <option key={mg.id} value={mg.id}>
                                      {mg.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            {/* Sets */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {t('exerciseTracker:form.sets')}
                                </label>
                                <button
                                  type="button"
                                  onClick={() => addSetToFocus(focusIndex)}
                                  className="text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400 flex items-center gap-1"
                                >
                                  <PlusIcon className="h-3 w-3" />
                                  {t('exerciseTracker:form.addSet')}
                                </button>
                              </div>

                              <div className="space-y-2">
                                {focus.sets.map((set, setIndex) => (
                                  <div
                                    key={setIndex}
                                    className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/30 rounded"
                                  >
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-6">
                                      {set.set_number}
                                    </span>

                                    {/* Weight fields (for weights category) */}
                                    {focus.category === 'weights' && (
                                      <>
                                        <input
                                          type="number"
                                          value={set.weight_value || ''}
                                          onChange={(e) => updateSet(focusIndex, setIndex, { weight_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                                          placeholder={t('exerciseTracker:form.weight')}
                                          className="w-20 px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                        />
                                        <select
                                          value={set.weight_unit || 'lbs'}
                                          onChange={(e) => updateSet(focusIndex, setIndex, { weight_unit: e.target.value })}
                                          className="w-16 px-1 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                        >
                                          <option value="lbs">lbs</option>
                                          <option value="kg">kg</option>
                                        </select>
                                        <span className="text-gray-400 dark:text-gray-500">×</span>
                                        <input
                                          type="number"
                                          value={set.reps || ''}
                                          onChange={(e) => updateSet(focusIndex, setIndex, { reps: e.target.value ? parseInt(e.target.value) : undefined })}
                                          placeholder={t('exerciseTracker:form.reps')}
                                          className="w-16 px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                        />
                                      </>
                                    )}

                                    {/* Cardio fields */}
                                    {focus.category === 'cardio' && (
                                      <>
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={set.distance_value || ''}
                                          onChange={(e) => updateSet(focusIndex, setIndex, { distance_value: e.target.value ? parseFloat(e.target.value) : undefined })}
                                          placeholder={t('exerciseTracker:form.distance')}
                                          className="w-20 px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                        />
                                        <select
                                          value={set.distance_unit || 'miles'}
                                          onChange={(e) => updateSet(focusIndex, setIndex, { distance_unit: e.target.value })}
                                          className="w-20 px-1 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                        >
                                          <option value="miles">miles</option>
                                          <option value="km">km</option>
                                          <option value="m">m</option>
                                        </select>
                                        <input
                                          type="number"
                                          value={set.duration_seconds ? Math.floor(set.duration_seconds / 60) : ''}
                                          onChange={(e) => updateSet(focusIndex, setIndex, { duration_seconds: e.target.value ? parseInt(e.target.value) * 60 : undefined })}
                                          placeholder={t('exerciseTracker:form.minutes')}
                                          className="w-16 px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                        />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">min</span>
                                      </>
                                    )}

                                    {/* Other/Stretching - just duration */}
                                    {(focus.category === 'stretching' || focus.category === 'other') && (
                                      <>
                                        <input
                                          type="number"
                                          value={set.duration_seconds || ''}
                                          onChange={(e) => updateSet(focusIndex, setIndex, { duration_seconds: e.target.value ? parseInt(e.target.value) : undefined })}
                                          placeholder={t('exerciseTracker:form.seconds')}
                                          className="w-20 px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-sm"
                                        />
                                        <span className="text-xs text-gray-500 dark:text-gray-400">sec</span>
                                      </>
                                    )}

                                    {/* Equipment selector */}
                                    <select
                                      value={set.equipment || ''}
                                      onChange={(e) => updateSet(focusIndex, setIndex, { equipment: e.target.value || undefined })}
                                      className="w-24 px-1 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded text-xs"
                                    >
                                      <option value="">{t('exerciseTracker:form.equipment')}</option>
                                      {equipmentOptions.map((eq) => (
                                        <option key={eq.id} value={eq.id}>
                                          {eq.name}
                                        </option>
                                      ))}
                                    </select>

                                    {/* Remove set */}
                                    {focus.sets.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => removeSet(focusIndex, setIndex)}
                                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Optional Fields */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('exerciseTracker:form.calories')}
                    </label>
                    <input
                      type="number"
                      value={totalCalories || ''}
                      onChange={(e) => setTotalCalories(e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('exerciseTracker:form.avgHeartRate')}
                    </label>
                    <input
                      type="number"
                      value={heartRateAvg || ''}
                      onChange={(e) => setHeartRateAvg(e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('exerciseTracker:form.maxHeartRate')}
                    </label>
                    <input
                      type="number"
                      value={heartRateMax || ''}
                      onChange={(e) => setHeartRateMax(e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="0"
                      className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('exerciseTracker:form.notes')}
                  </label>
                  <textarea
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    placeholder={t('exerciseTracker:form.notesPlaceholder')}
                    rows={2}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg resize-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={quickLogSession}
                    disabled={loading || !focuses.some(f => f.exercise_name.trim())}
                    className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <CheckIcon className="h-5 w-5" />
                    {t('exerciseTracker:form.saveWorkout')}
                  </button>
                </div>

                {/* Save as Template */}
                {!activeSession && focuses.some(f => f.exercise_name.trim()) && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    {!showTemplateForm ? (
                      <button
                        type="button"
                        onClick={() => setShowTemplateForm(true)}
                        className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 flex items-center gap-1"
                      >
                        <BookmarkIcon className="h-4 w-4" />
                        {t('exerciseTracker:form.saveAsTemplate')}
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                          placeholder={t('exerciseTracker:form.templateName')}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                        />
                        <input
                          type="text"
                          value={templateDescription}
                          onChange={(e) => setTemplateDescription(e.target.value)}
                          placeholder={t('exerciseTracker:form.templateDescription')}
                          className="w-full px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={saveAsTemplate}
                            disabled={!templateName.trim()}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 text-sm"
                          >
                            {t('exerciseTracker:form.save')}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowTemplateForm(false);
                              setTemplateName('');
                              setTemplateDescription('');
                            }}
                            className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm"
                          >
                            {t('exerciseTracker:form.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <select
                value={historyDays}
                onChange={(e) => setHistoryDays(parseInt(e.target.value))}
                className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
              >
                <option value={7}>{t('exerciseTracker:history.last7Days')}</option>
                <option value={14}>{t('exerciseTracker:history.last14Days')}</option>
                <option value={30}>{t('exerciseTracker:history.last30Days')}</option>
                <option value={90}>{t('exerciseTracker:history.last90Days')}</option>
              </select>
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                {sessionHistory.length} {t('exerciseTracker:history.sessions')}
              </span>
            </div>

            {sessionHistory.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                <CalendarIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">{t('exerciseTracker:history.noSessions')}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  {t('exerciseTracker:history.startLogging')}
                </p>
              </div>
            ) : (
              sessionHistory.map((session) => {
                const isExpanded = expandedSessionId === session.id;
                const details = expandedSessionDetails[session.id];

                return (
                  <div key={session.id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    {/* Session Header - Clickable */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750"
                      onClick={() => toggleSessionExpand(session.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium text-gray-900 dark:text-gray-100 capitalize">
                              {session.exercise_type}
                            </h3>
                            {session.total_duration_minutes && (
                              <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs">
                                {formatDurationMinutes(session.total_duration_minutes)}
                              </span>
                            )}
                            {session.total_calories && (
                              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-xs">
                                {session.total_calories} cal
                              </span>
                            )}
                            {session.heart_rate_avg && (
                              <span className="px-2 py-0.5 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded-full text-xs">
                                {session.heart_rate_avg} bpm avg
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-1">
                                <CalendarIcon className="h-4 w-4" />
                                {formatDate(session.start_time)}
                              </div>
                              <div className="flex items-center gap-1">
                                <ClockIcon className="h-4 w-4" />
                                {formatTimeShort(session.start_time)}
                                {session.end_time && ` - ${formatTimeShort(session.end_time)}`}
                              </div>
                            </div>
                            {session.notes && (
                              <p className="mt-2 text-gray-500 dark:text-gray-400 italic text-sm">
                                "{session.notes}"
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSession(session.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                          {isExpanded ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-4">
                        {!details ? (
                          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                            Loading details...
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Session Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {details.total_duration_minutes && (
                                <div className="bg-white dark:bg-gray-800/80 rounded-lg p-3">
                                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Duration</div>
                                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {formatDurationMinutes(details.total_duration_minutes)}
                                  </div>
                                </div>
                              )}
                              {details.total_calories && (
                                <div className="bg-white dark:bg-gray-800/80 rounded-lg p-3">
                                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Calories</div>
                                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {details.total_calories} kcal
                                  </div>
                                </div>
                              )}
                              {details.heart_rate_avg && (
                                <div className="bg-white dark:bg-gray-800/80 rounded-lg p-3">
                                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Avg Heart Rate</div>
                                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {details.heart_rate_avg} bpm
                                  </div>
                                </div>
                              )}
                              {details.heart_rate_max && (
                                <div className="bg-white dark:bg-gray-800/80 rounded-lg p-3">
                                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Max Heart Rate</div>
                                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {details.heart_rate_max} bpm
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Exercises/Focuses */}
                            {details.focuses && details.focuses.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                                  Exercises ({details.focuses.length})
                                </h4>
                                {details.focuses.map((focus, focusIdx) => (
                                  <div key={focusIdx} className="bg-white dark:bg-gray-800/80 rounded-lg p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="font-medium text-gray-900 dark:text-gray-100">
                                        {focus.exercise_name}
                                      </span>
                                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs capitalize">
                                        {focus.category}
                                      </span>
                                      {focus.muscle_group && (
                                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs capitalize">
                                          {focus.muscle_group.replace('_', ' ')}
                                        </span>
                                      )}
                                    </div>
                                    {focus.notes && (
                                      <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-2">
                                        {focus.notes}
                                      </p>
                                    )}

                                    {/* Sets */}
                                    {focus.sets && focus.sets.length > 0 && (
                                      <div className="mt-2">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                                              <th className="text-left py-1 pr-2">Set</th>
                                              {focus.category === 'weights' && (
                                                <>
                                                  <th className="text-left py-1 pr-2">Weight</th>
                                                  <th className="text-left py-1 pr-2">Reps</th>
                                                </>
                                              )}
                                              {focus.category === 'cardio' && (
                                                <>
                                                  <th className="text-left py-1 pr-2">Distance</th>
                                                  <th className="text-left py-1 pr-2">Time</th>
                                                  <th className="text-left py-1 pr-2">Pace</th>
                                                </>
                                              )}
                                              {focus.sets[0]?.equipment && <th className="text-left py-1 pr-2">Equipment</th>}
                                              {focus.sets[0]?.heart_rate && <th className="text-left py-1 pr-2">HR</th>}
                                              {focus.sets[0]?.calories && <th className="text-left py-1 pr-2">Cal</th>}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {focus.sets.map((set, setIdx) => (
                                              <tr key={setIdx} className="text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700">
                                                <td className="py-1.5 pr-2 font-medium">{set.set_number}</td>
                                                {focus.category === 'weights' && (
                                                  <>
                                                    <td className="py-1.5 pr-2">
                                                      {set.weight_value ? `${set.weight_value} ${set.weight_unit || 'lbs'}` : '-'}
                                                    </td>
                                                    <td className="py-1.5 pr-2">{set.reps || '-'}</td>
                                                  </>
                                                )}
                                                {focus.category === 'cardio' && (
                                                  <>
                                                    <td className="py-1.5 pr-2">
                                                      {set.distance_value ? `${set.distance_value} ${set.distance_unit || 'mi'}` : '-'}
                                                    </td>
                                                    <td className="py-1.5 pr-2">
                                                      {set.duration_seconds
                                                        ? `${Math.floor(set.duration_seconds / 60)}:${String(set.duration_seconds % 60).padStart(2, '0')}`
                                                        : '-'}
                                                    </td>
                                                    <td className="py-1.5 pr-2">
                                                      {set.pace_per_unit ? `${set.pace_per_unit}/${set.distance_unit || 'mi'}` : '-'}
                                                    </td>
                                                  </>
                                                )}
                                                {focus.sets[0]?.equipment && (
                                                  <td className="py-1.5 pr-2 capitalize">{set.equipment?.replace('_', ' ') || '-'}</td>
                                                )}
                                                {focus.sets[0]?.heart_rate && (
                                                  <td className="py-1.5 pr-2">{set.heart_rate ? `${set.heart_rate} bpm` : '-'}</td>
                                                )}
                                                {focus.sets[0]?.calories && (
                                                  <td className="py-1.5 pr-2">{set.calories || '-'}</td>
                                                )}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* No exercises message */}
                            {(!details.focuses || details.focuses.length === 0) && (
                              <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                                No exercise details recorded for this session
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Charts Tab */}
        {activeTab === 'charts' && (
          <div className="space-y-6">
            {/* Stats Summary - period-specific except streak */}
            {(() => {
              const periodStats = getPeriodStats();
              const periodLabel = chartPeriod === 'week' ? 'This Week' : chartPeriod === 'month' ? 'This Month' : 'Year to Date';
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{periodStats.totalSessions}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('exerciseTracker:charts.totalSessions')}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{periodLabel}</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {periodStats.totalDurationMinutes >= 60
                        ? `${Math.floor(periodStats.totalDurationMinutes / 60)}h ${periodStats.totalDurationMinutes % 60}m`
                        : `${periodStats.totalDurationMinutes}m`}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('exerciseTracker:charts.totalTime')}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{periodLabel}</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {periodStats.totalCalories.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('exerciseTracker:charts.totalCalories')}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{periodLabel}</div>
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{statistics?.streak_days || 0}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('exerciseTracker:charts.streakDays')}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">Current Streak</div>
                  </div>
                </div>
              );
            })()}

            {/* Activity Chart with Period Selector */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {chartPeriod === 'week' ? t('exerciseTracker:charts.weeklyActivity') :
                   chartPeriod === 'month' ? 'Monthly Activity' : 'Year to Date Activity'}
                </h3>
                <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setChartPeriod('week')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      chartPeriod === 'week'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setChartPeriod('month')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      chartPeriod === 'month'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setChartPeriod('ytd')}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      chartPeriod === 'ytd'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    YTD
                  </button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={getActivityChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="label"
                    stroke="#9CA3AF"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={chartPeriod === 'month' ? -15 : 0}
                    textAnchor={chartPeriod === 'month' ? 'end' : 'middle'}
                    height={chartPeriod === 'week' ? 50 : 40}
                  />
                  <YAxis stroke="#9CA3AF" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#F9FAFB' }}
                    formatter={(value: number) => [`${value} session${value !== 1 ? 's' : ''}`, 'Sessions']}
                  />
                  <Bar dataKey="sessions" fill="#F97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Exercise Type Distribution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  {t('exerciseTracker:charts.byType')}
                </h3>
                {getSessionsByTypeData().length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={getSessionsByTypeData()}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {getSessionsByTypeData().map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-gray-500 dark:text-gray-400">
                    No data available
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  {t('exerciseTracker:charts.byCategory')}
                </h3>
                {getSessionsByCategoryData().length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={getSessionsByCategoryData()}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {getSessionsByCategoryData().map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-gray-500 dark:text-gray-400">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Analysis Tab */}
        {activeTab === 'analysis' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('exerciseTracker:analysis.title')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('exerciseTracker:analysis.description')}
                  </p>
                </div>
                <select
                  value={historyDays}
                  onChange={(e) => setHistoryDays(parseInt(e.target.value))}
                  className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
                >
                  <option value={7}>{t('exerciseTracker:history.last7Days')}</option>
                  <option value={14}>{t('exerciseTracker:history.last14Days')}</option>
                  <option value={30}>{t('exerciseTracker:history.last30Days')}</option>
                  <option value={90}>{t('exerciseTracker:history.last90Days')}</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAnalyze}
                  disabled={loading || sessionHistory.length === 0}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {t('exerciseTracker:analysis.analyze')}
                </button>
                <button
                  onClick={handleGenerateReport}
                  disabled={loading || sessionHistory.length === 0}
                  className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
                >
                  <DocumentTextIcon className="h-5 w-5" />
                  {t('exerciseTracker:analysis.generateReport')}
                </button>
              </div>
            </div>

            {/* Analysis Results */}
            {analysis && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
                {/* Header with Export/Print buttons */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    Analysis Results
                  </h4>
                  <div className="flex items-center gap-2">
                    <ExportDropdownMenu
                      onExport={handleExportAnalysis}
                      onPrint={printAnalysis}
                      showPrint={true}
                    />
                    <button
                      onClick={printAnalysis}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Print analysis"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5Zm-3 0h.008v.008H15V10.5Z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setAnalysis(null)}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Clear analysis"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('exerciseTracker:analysis.summary')}
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">{analysis.summary}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t('exerciseTracker:analysis.analyzed', {
                      count: analysis.total_sessions_analyzed,
                      days: analysis.analysis_period_days,
                    })}
                  </p>
                </div>

                {analysis.patterns_identified && analysis.patterns_identified.length > 0 && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                      {t('exerciseTracker:analysis.patterns')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {analysis.patterns_identified.map((pattern, i) => (
                        <li key={i} className="text-sm text-blue-700 dark:text-blue-300">
                          {pattern}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.progress_insights && analysis.progress_insights.length > 0 && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">
                      {t('exerciseTracker:analysis.strengths')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {analysis.progress_insights.map((insight, i) => (
                        <li key={i} className="text-sm text-green-700 dark:text-green-300">
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.recommendations && analysis.recommendations.length > 0 && (
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <h4 className="font-medium text-orange-800 dark:text-orange-200 mb-2 flex items-center">
                      <LightBulbIcon className="h-5 w-5 mr-2" />
                      {t('exerciseTracker:analysis.recommendations')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {analysis.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm text-orange-700 dark:text-orange-300">
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.recovery_suggestions && analysis.recovery_suggestions.length > 0 && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">
                      Recovery Suggestions
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {analysis.recovery_suggestions.map((suggestion, i) => (
                        <li key={i} className="text-sm text-purple-700 dark:text-purple-300">
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {analysis.resource_links && analysis.resource_links.length > 0 && (
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                    <h4 className="font-medium text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
                      <LinkIcon className="h-5 w-5 mr-2" />
                      Fitness Resources
                    </h4>
                    <ul className="space-y-2">
                      {analysis.resource_links.map((link, i) => (
                        <li key={i} className="text-sm">
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                          >
                            {link.title}
                          </a>
                          <span className="text-indigo-500 dark:text-indigo-300 ml-2">
                            — {link.source}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Report Preview */}
            {reportContent && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    {t('exerciseTracker:analysis.reportPreview')}
                  </h4>
                  <ExportDropdownMenu onExport={handleExport} onPrint={printReport} showPrint={true} />
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 font-mono">
                    {reportContent}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {t('exerciseTracker:templates.title')}
              </h3>
            </div>

            {templates.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                <BookmarkIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">{t('exerciseTracker:templates.noTemplates')}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  {t('exerciseTracker:templates.createHint')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <div key={template.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{template.name}</h4>
                        {template.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {template.description}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1 mt-2">
                          <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded text-xs">
                            {template.exercise_type}
                          </span>
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                            {template.template_data.focuses?.length || 0} {t('exerciseTracker:templates.exercises')}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => applyTemplate(template)}
                          className="p-2 text-orange-600 hover:text-orange-700 dark:text-orange-400"
                          title={t('exerciseTracker:templates.apply')}
                        >
                          <PlayIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => deleteTemplate(template.id)}
                          className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          title={t('exerciseTracker:templates.delete')}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-4xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('exerciseTracker:disclaimer.title')}:</strong> {t('exerciseTracker:disclaimer.text')}
        </p>
      </div>

      {/* Loading Overlay */}
      <LoadingOverlay isVisible={loading} message={loadingMessage} />
    </div>
  );
};
